import { useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import { ROLE_LABELS } from "../lib/roles";

export default function Users() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", nombre: "", password: "", roles: [] });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [u, r] = await Promise.all([
                api.get("/users"),
                api.get("/users/roles"),
            ]);
            setUsers(u.data);
            setRoles(r.data);
        } catch (err) {
            toast.error("Sin acceso a usuarios", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggleRole = (rv) => {
        setForm((f) => ({
            ...f,
            roles: f.roles.includes(rv)
                ? f.roles.filter((x) => x !== rv)
                : [...f.roles, rv],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/users", form);
            toast.success("Usuario creado");
            setOpen(false);
            setForm({ email: "", nombre: "", password: "", roles: [] });
            load();
        } catch (err) {
            toast.error("No se pudo crear", { description: err?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const toggleActivo = async (u) => {
        try {
            await api.patch(`/users/${u.id}`, { activo: !u.activo });
            toast.success(`${u.email} ${u.activo ? "desactivado" : "activado"}`);
            load();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1400px]" data-testid="users-page">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Admin TI
                    </div>
                    <h1 className="font-display text-4xl tracking-tight">Usuarios y roles</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gestión de accesos, roles RBAC y estado de cuentas.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-user-button">
                            <UserPlus className="h-4 w-4 mr-2" /> Nuevo usuario
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Crear usuario</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3" data-testid="new-user-form">
                            <div>
                                <Label>Nombre</Label>
                                <Input
                                    value={form.nombre}
                                    onChange={(e) =>
                                        setForm({ ...form, nombre: e.target.value })
                                    }
                                    required
                                    data-testid="new-user-nombre"
                                />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    required
                                    data-testid="new-user-email"
                                />
                            </div>
                            <div>
                                <Label>Password (mín. 8)</Label>
                                <Input
                                    type="password"
                                    minLength={8}
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm({ ...form, password: e.target.value })
                                    }
                                    required
                                    data-testid="new-user-password"
                                />
                            </div>
                            <div>
                                <Label>Roles</Label>
                                <div className="grid grid-cols-2 gap-1.5 mt-1 max-h-56 overflow-y-auto border border-border rounded-sm p-2">
                                    {roles.map((r) => (
                                        <label
                                            key={r.value}
                                            className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-muted rounded-sm px-1.5"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.roles.includes(r.value)}
                                                onChange={() => toggleRole(r.value)}
                                                data-testid={`new-user-role-${r.value}`}
                                            />
                                            <span>{r.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="new-user-submit"
                                >
                                    {busy ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        "Crear"
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="users-table">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Nombre</th>
                            <th>Roles</th>
                            <th>MFA</th>
                            <th>Activo</th>
                            <th>Creado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.email}`}>
                                <td className="font-medium text-sm">{u.email}</td>
                                <td className="text-sm">{u.nombre}</td>
                                <td>
                                    <div className="flex flex-wrap gap-1">
                                        {(u.roles || []).map((r) => (
                                            <span key={r} className="badge-gold">
                                                {ROLE_LABELS[r] || r}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td>
                                    {u.mfa_enrolled ? (
                                        <span className="badge-exito">enrolado</span>
                                    ) : (
                                        <span className="badge-mock">pendiente</span>
                                    )}
                                </td>
                                <td>
                                    <Switch
                                        checked={u.activo}
                                        onCheckedChange={() => toggleActivo(u)}
                                        data-testid={`toggle-activo-${u.email}`}
                                    />
                                </td>
                                <td className="text-[11px] text-muted-foreground tabular">
                                    {u.created_at?.slice(0, 10)}
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && !loading && (
                            <tr>
                                <td colSpan={6} className="text-center py-10 text-muted-foreground">
                                    Sin usuarios.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
